/**
 * Main router for ludicrous-gateway
 * Routes requests to providers based on rules and analysis
 */

import type { MessagesRequest } from "../types/messages.js";
import type {
  ContentAnalysis,
  RoutingDecision,
  CompiledRoutingRule,
  Router,
} from "../types/routing.js";
import type { RoutingConfig, RoutingRuleConfig } from "../types/config.js";
import { compileRules } from "./rules.js";
import { findMatchingRule, evaluateAllRules } from "./rule-evaluator.js";
import { getLogger } from "../utils/logger.js";

/**
 * Router configuration
 */
export interface RouterConfig {
  /** Default provider when no rules match */
  defaultProvider: string;
  /** Default model for the default provider */
  defaultModel?: string | undefined;
  /** Routing rules */
  rules: RoutingRuleConfig[];
}

/**
 * Default router implementation
 */
export class DefaultRouter implements Router {
  private config: RouterConfig;
  private compiledRules: CompiledRoutingRule[];

  constructor(config: RouterConfig) {
    this.config = config;
    this.compiledRules = compileRules(config.rules);

    const logger = getLogger();
    logger.debug(`Router initialized with ${this.compiledRules.length} rules`);
  }

  /**
   * Route a request to a provider
   */
  route(request: MessagesRequest, analysis: ContentAnalysis): RoutingDecision {
    const logger = getLogger();

    // Try to find a matching rule
    const match = findMatchingRule(this.compiledRules, analysis);

    if (match) {
      logger.debug(`Rule matched: ${match.rule.name}`, {
        conditions: match.evaluation.matchedConditions,
      });

      return {
        provider: match.rule.destinationProvider,
        model: match.rule.destinationModel ?? request.model,
        matchedRule: match.rule.name,
        reason: `Matched rule '${match.rule.name}' (${match.evaluation.matchedConditions.join(", ")})`,
        isDefault: false,
        analysis,
      };
    }

    // No rules matched, use default
    logger.debug("No rules matched, using default provider");

    return {
      provider: this.config.defaultProvider,
      model: this.config.defaultModel ?? request.model,
      reason: "No routing rules matched, using default provider",
      isDefault: true,
      analysis,
    };
  }

  /**
   * Get all compiled rules
   */
  getRules(): CompiledRoutingRule[] {
    return [...this.compiledRules];
  }

  /**
   * Reload rules from new configuration
   */
  reloadRules(): void {
    this.compiledRules = compileRules(this.config.rules);
    getLogger().info(`Reloaded ${this.compiledRules.length} routing rules`);
  }

  /**
   * Update rules from new configuration
   */
  updateConfig(config: RouterConfig): void {
    this.config = config;
    this.compiledRules = compileRules(config.rules);
    getLogger().info(`Updated router config with ${this.compiledRules.length} rules`);
  }

  /**
   * Debug routing decision (evaluate all rules)
   */
  debugRoute(
    request: MessagesRequest,
    analysis: ContentAnalysis
  ): {
    decision: RoutingDecision;
    ruleEvaluations: Array<{
      ruleName: string;
      matched: boolean;
      matchedConditions: string[];
      failedConditions: string[];
    }>;
  } {
    const evaluations = evaluateAllRules(this.compiledRules, analysis);
    const decision = this.route(request, analysis);

    return {
      decision,
      ruleEvaluations: evaluations.map((e) => ({
        ruleName: e.rule.name,
        matched: e.evaluation.matched,
        matchedConditions: e.evaluation.matchedConditions,
        failedConditions: e.evaluation.failedConditions,
      })),
    };
  }
}

/**
 * Create a router from gateway routing config
 */
export function createRouter(config: RoutingConfig): DefaultRouter {
  return new DefaultRouter({
    defaultProvider: config.default_provider,
    defaultModel: config.default_model,
    rules: config.rules,
  });
}

// Re-export rule utilities
export { compileRules, compileRule, validateRule, defaultRules } from "./rules.js";
export {
  evaluateRule,
  findMatchingRule,
  evaluateAllRules,
} from "./rule-evaluator.js";
